import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ProductCard } from "@/components/store/ProductCard";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronRight, Home } from "lucide-react";

interface Category {
  id: string;
  name: string;
  slug: string | null;
  parent_id: string | null;
  image_url: string | null;
}

interface Product {
  id: string;
  name: string;
  price: number;
  image_url: string | null;
  stock: number;
  category: string | null;
  category_id: string | null;
  featured: boolean;
  on_sale: boolean | null;
  sale_price: number | null;
}

const CategoryPage = () => {
  const { parent, child } = useParams();
  const activeSlug = (child || parent || "").toLowerCase();
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [{ data: cats }, { data: prods }] = await Promise.all([
        supabase.from("categories").select("*").order("display_order"),
        supabase.from("products").select("*").order("created_at", { ascending: false }),
      ]);
      setCategories((cats as Category[]) ?? []);
      setProducts((prods as Product[]) ?? []);
      setLoading(false);
    };
    load();
  }, []);

  const parentCat = useMemo(
    () => categories.find((c) => (c.slug ?? "").toLowerCase() === (parent ?? "").toLowerCase()),
    [categories, parent],
  );
  const activeCat = useMemo(
    () => categories.find((c) => (c.slug ?? "").toLowerCase() === activeSlug),
    [categories, activeSlug],
  );
  const subCategories = useMemo(
    () => (activeCat ? categories.filter((c) => c.parent_id === activeCat.id) : []),
    [categories, activeCat],
  );

  const filtered = useMemo(() => {
    if (!activeCat) return [];
    const ids = new Set<string>([activeCat.id]);
    // include descendants if top-level
    categories.filter((c) => c.parent_id === activeCat.id).forEach((c) => ids.add(c.id));
    return products.filter(
      (p) =>
        (p.category_id && ids.has(p.category_id)) ||
        (p.category ?? "").toLowerCase() === activeCat.name.toLowerCase(),
    );
  }, [products, categories, activeCat]);

  return (
    <section className="container py-6 md:py-10">
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4">
        <Link to="/" className="inline-flex items-center gap-1 hover:text-primary">
          <Home className="h-3 w-3" /> Home
        </Link>
        <ChevronRight className="h-3 w-3" />
        {parentCat && parentCat !== activeCat && (
          <>
            <Link to={`/category/${parentCat.slug}`} className="hover:text-primary">
              {parentCat.name}
            </Link>
            <ChevronRight className="h-3 w-3" />
          </>
        )}
        <span className="text-foreground font-medium">
          {activeCat?.name ?? activeSlug}
        </span>
      </nav>

      <h1 className="font-display text-2xl md:text-3xl font-bold mb-2">
        {activeCat?.name ?? "Category"}
      </h1>

      {subCategories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {subCategories.map((sc) => (
            <Link
              key={sc.id}
              to={`/category/${activeCat!.slug}/${sc.slug}`}
              className="rounded-full border border-border/60 bg-background px-3 py-1.5 text-xs font-medium hover:border-primary hover:text-primary transition-colors"
            >
              {sc.name}
            </Link>
          ))}
        </div>
      )}

      {loading ? (
        <div className="grid gap-3 sm:gap-5 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-2xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <p className="py-20 text-center text-muted-foreground">
          No products in this category yet.
        </p>
      ) : (
        <div className="grid gap-3 sm:gap-5 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {filtered.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </section>
  );
};

export default CategoryPage;
